import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { resizeImage } from '@troverecommerce/saas-components-ts';

interface ImageData {
  url: string;
  file?: File;
  key: string;
}

interface ImageUploadProps {
  images: Record<string, string>; // Current images { key: url }
  onImagesChange: (
    images: Record<string, string>,
    files: Record<string, File>,
    imageKeys: string[],
  ) => void;
  maxFileSize?: number; // in MB
  disabled?: boolean;
  keyPrefix?: string; // Optional prefix for auto-generated keys (e.g., 'studio_')
  reservedKeys?: Record<string, string>; // Keys that should not be used
}

const MAX_FILE_SIZE_MB = 10;

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.ogv'];

const isVideoUrl = (url: string): boolean => {
  if (url.includes('/video/upload/')) return true;
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return VIDEO_EXTENSIONS.some(ext => pathname.endsWith(ext));
  } catch {
    return VIDEO_EXTENSIONS.some(ext => url.toLowerCase().endsWith(ext));
  }
};

/**
 * ImageUpload component for managing keyed images
 * Supports upload, delete, and preview with inline upload zone
 */
export const ImageUpload = ({
  images,
  onImagesChange,
  maxFileSize = MAX_FILE_SIZE_MB,
  disabled = false,
  keyPrefix = '',
  reservedKeys = {},
}: ImageUploadProps) => {
  const [localImages, setLocalImages] = useState<Record<string, ImageData>>(
    () => {
      const initial: Record<string, ImageData> = {};
      Object.entries(images).forEach(([key, url]) => {
        initial[key] = { key, url };
      });
      return initial;
    },
  );

  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Update localImages when images prop changes (for loading existing images)
  useEffect(() => {
    const newLocalImages: Record<string, ImageData> = {};
    Object.entries(images).forEach(([key, url]) => {
      newLocalImages[key] = { key, url };
    });
    setLocalImages(newLocalImages);
  }, [images]);

  const notifyParent = useCallback(
    (
      updatedImages: Record<string, ImageData>,
      updatedFiles: Record<string, File>,
    ) => {
      const imageUrls: Record<string, string> = {};
      const imageKeys: string[] = [];

      Object.entries(updatedImages).forEach(([key, data]) => {
        imageUrls[key] = data.url;
        imageKeys.push(key);
      });

      onImagesChange(imageUrls, updatedFiles, imageKeys);
    },
    [onImagesChange],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFiles = (files: File[]) => {
    setUploadError(null);

    const newImages = { ...localImages };
    const newFiles = { ...pendingFiles };
    let hasError = false;

    files.forEach((file, index) => {
      // Validate file size
      if (file.size > maxFileSize * 1024 * 1024) {
        setUploadError(`File ${file.name} exceeds ${maxFileSize}MB limit`);
        hasError = true;
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        setUploadError(`File ${file.name} is not an image or video`);
        hasError = true;
        return;
      }

      // Generate a unique key for the new image, avoiding reserved keys
      const mainKey = keyPrefix ? `${keyPrefix}main` : 'main';
      let key = index === 0 && !newImages[mainKey] && !reservedKeys[mainKey] ? mainKey :
                keyPrefix ? `${keyPrefix}alt${Object.keys(newImages).length}` : `alt${Object.keys(newImages).length}`;
      let counter = 1;
      while (newImages[key] || reservedKeys[key]) {
        key = keyPrefix ? `${keyPrefix}alt${counter}` : `alt${counter}`;
        counter++;
      }

      // Create blob URL for preview
      const blobUrl = URL.createObjectURL(file);

      newImages[key] = {
        key,
        url: blobUrl,
        file,
      };

      newFiles[key] = file;
    });

    if (!hasError) {
      setLocalImages(newImages);
      setPendingFiles(newFiles);
      notifyParent(newImages, newFiles);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    handleFiles(Array.from(files));
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (!files || files.length === 0) return;

    handleFiles(Array.from(files));
  };

  const handleDeleteImage = (key: string) => {
    const newImages = { ...localImages };
    const newFiles = { ...pendingFiles };

    // Revoke blob URL if it exists
    if (newImages[key]?.file && newImages[key].url.startsWith('blob:')) {
      URL.revokeObjectURL(newImages[key].url);
    }

    delete newImages[key];
    delete newFiles[key];

    setLocalImages(newImages);
    setPendingFiles(newFiles);
    notifyParent(newImages, newFiles);
  };

  // Sort images to ensure 'main' comes first, then alphabetically
  const imageEntries = Object.entries(localImages).sort(([keyA], [keyB]) => {
    if (keyA === 'main') return -1;
    if (keyB === 'main') return 1;
    return keyA.localeCompare(keyB);
  });

  return (
    <Box>
      {/* Error Display */}
      {uploadError && (
        <Typography color='error' variant='body2' sx={{ mb: 2 }}>
          {uploadError}
        </Typography>
      )}

      {/* Image Gallery + Inline Upload Zone */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {/* Image Cards */}
        {imageEntries.map(([key, imageData]) => {
          const isVideo =
            isVideoUrl(imageData.url) ||
            imageData.file?.type.startsWith('video/');

          // Don't apply resizeImage to video URLs - it mangles them
          const displayUrl =
            imageData.url.startsWith('blob:') || isVideo ?
              imageData.url : resizeImage(imageData.url, 800);

          return (
            <Card key={key} sx={{ width: 200 }}>
              {isVideo ? (
                /* eslint-disable-next-line jsx-a11y/media-has-caption */
                <video
                  controls
                  preload='metadata'
                  src={displayUrl}
                  style={{
                    width: '100%',
                    height: 150,
                    objectFit: 'contain',
                    display: 'block',
                    background: '#000',
                  }}
                />
              ) : (
                <CardMedia
                  component='img'
                  height='150'
                  image={displayUrl}
                  alt={key}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardActions sx={{ justifyContent: 'space-between', px: 1 }}>
                <Typography variant='caption' sx={{ fontWeight: 'bold' }}>
                  {key}
                </Typography>
                <Box>
                  <IconButton
                    size='small'
                    onClick={async () => {
                      try {
                        const res = await fetch(imageData.url);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = key;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(blobUrl);
                      } catch {
                        window.open(imageData.url, '_blank');
                      }
                    }}
                    title='Download'
                  >
                    <DownloadIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => handleDeleteImage(key)}
                    disabled={disabled}
                    color='error'
                    title='Delete image'
                  >
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          );
        })}

        {/* Inline Upload Zone */}
        {!disabled && (
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              width: 200,
              height: 220,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              cursor: 'pointer',
              px: 2,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <UploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 1 }} />
            <Typography variant='body2' align='center' gutterBottom>
              Drag and drop files here
            </Typography>
            <Button
              variant='contained'
              component='label'
              size='small'
              sx={{ mt: 1 }}
            >
              Choose Files
              <input
                type='file'
                hidden
                multiple
                accept='image/*,video/*'
                onChange={handleFileSelect}
              />
            </Button>
            <Typography
              variant='caption'
              color='text.secondary'
              align='center'
              sx={{ mt: 1 }}
            >
              Maximum file size: {maxFileSize}
              MB per file
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
